import {Controller, useForm} from "react-hook-form";
import React, {useCallback, useEffect, useState} from "react";
import {ContentWrapper, Error, RateBox} from "../styles";
import CustomSelect from "../../../../components/common/CustomSelect";
import InputAndSelect from "../../../../components/common/InputAndSelect";
import { BalanceExceptionWrapper } from "../../Wallet/ChinaPayoutV2/styles";
import {ReactComponent as NotifyIcon} from "../../../../assets/icons/notify-icon.svg";
import {Link} from "react-router-dom";
import {useAppDispatch, useAppSelector} from "../../../../app/hooks";
import {Button} from "../../../../components/common/Button";
import {ghsPayoutMethods} from "../../const";
import {getPayoutTransferFee, getMobileMoneyTransferFee} from "../../redux/slice";
import {formatCurrencyAmount} from "../../../../utils/currency";
import Loader from "../../../../components/common/Loader";
import {savedPayoutV2Data} from "../../redux/formDataSlice";
import {usePopulateFormFromSavedData} from "../hooks";
import { capitalizeCurrencyCode, isValidMinimumAmount } from "../../../../utils/helpers";
import { GHS_MINIMUM_PAYOUT_AMOUNT } from "./constants";

type AmountType = {
    phoneNumber?: string;
    paymentMethod?: string;
    amount?: string | number;
    handleNext?: () => void;
    currency?: string;
    totalAmount?: number;
    balanceError?: boolean;
    fees?: number;
    feeRate?: number | string;
    feeType?: string;
};

const SELECTED_PAYMENT_METHOD = "BANK_ACCOUNT";

const getMinimumAmountMessage = () =>
    `Minimum amount: ${capitalizeCurrencyCode("GHS")} ${new Intl.NumberFormat().format(GHS_MINIMUM_PAYOUT_AMOUNT)}`;

const Amount = ({handleNext}: AmountType) => {
    const {
        handleSubmit,
        control,
        setValue,
        watch,
        formState: {errors, isValid},
    } = useForm<AmountType>();

    const {wallets, loading} = useAppSelector((state) => state?.wallets || {});
    const currentWallet = wallets?.find((wallet) => wallet?.currency === "GHS")

    const getSavedData = useAppSelector((state) => state.formData.payoutv2Data);

    const dispatch = useAppDispatch();

    const paymentMethod = watch("paymentMethod");
    const amount = watch("amount");

    const INT_FORM_DATA = useAppSelector(
        (state) => state.formData?.payoutv2Data || {},
    );
    const [userData, setUserData] = useState<AmountType>(INT_FORM_DATA);

    const handleDataUpdate = (field: keyof AmountType, value: any) => {
        setUserData((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const uniqueCurrencies = Array.from(
        new Set(wallets?.map((wallet) => wallet.currency)),
    );

    const options = uniqueCurrencies.map((currency) => ({
        label: currency,
        value: currency,
    }));

    const walletToDebit = options?.filter((option) => option.value === "GHS");

    const walletBalance = Number(currentWallet?.balance) || 0;
    const totalAmountValue = Number(userData?.totalAmount) || 0;

    const hasBalanceError = totalAmountValue > walletBalance;
    const hasEnteredAmount =
        amount !== undefined && amount !== null && amount !== "";
    const isBelowMinimumAmount =
        hasEnteredAmount &&
        !isValidMinimumAmount(amount, GHS_MINIMUM_PAYOUT_AMOUNT);
    const canContinueWithAmount = isValidMinimumAmount(
        amount,
        GHS_MINIMUM_PAYOUT_AMOUNT,
    );

    useEffect(() => {
        (async () => {
            const isMobileMoney = paymentMethod && paymentMethod !== SELECTED_PAYMENT_METHOD;

            if (isMobileMoney) {
                const resultAction = await dispatch(getMobileMoneyTransferFee("GH"));
                if (getMobileMoneyTransferFee.fulfilled.match(resultAction)) {
                    const {fees, type} = resultAction.payload;
                    handleDataUpdate("feeRate", fees);
                    handleDataUpdate("feeType", type);
                }
            } else {
                const resultAction = await dispatch(getPayoutTransferFee("GH"));
                if (getPayoutTransferFee.fulfilled.match(resultAction)) {
                    const {fees, type} = resultAction.payload;
                    handleDataUpdate("feeRate", fees);
                    handleDataUpdate("feeType", type);
                }
            }
        })();
    }, [paymentMethod, amount]);

    usePopulateFormFromSavedData(getSavedData, setValue);

    const handleNextStep = useCallback(
        (data: AmountType) => {
            const nextAmount = data.amount ?? userData.amount;
            if (!isValidMinimumAmount(nextAmount, GHS_MINIMUM_PAYOUT_AMOUNT)) {
                return;
            }

            const selectedBank = ghsPayoutMethods?.find(
                (bank) => bank.value === data.paymentMethod,
            );

            const combinedData = {
                ...userData,
                ...data,
                amount: Number(nextAmount),
                paymentMethodName: selectedBank?.label,
                paymentMethodValue: selectedBank?.value,
            };

            dispatch(savedPayoutV2Data(combinedData));
            handleNext?.();
        },
        [dispatch, handleNext, userData],
    );

    useEffect(() => {
        if (userData.amount && userData.feeRate) {
            let calculatedFee = 0;

            if (userData.feeType === "PERCENTAGE") {
                calculatedFee = (Number(userData.amount) * Number(userData.feeRate)) / 100;
            } else {
                calculatedFee = Number(userData.feeRate);
            }

            const totalAmount = Number(userData.amount) + calculatedFee;
            handleDataUpdate("totalAmount", totalAmount);

            handleDataUpdate("fees", calculatedFee);
        }
    }, [userData.amount, userData.feeRate, userData.feeType]);

    return (
        <ContentWrapper>

            <div>
                <Controller
                    name="amount"
                    control={control}
                    rules={{
                        required: "Amount is required",
                        validate: (value) =>
                            isValidMinimumAmount(value, GHS_MINIMUM_PAYOUT_AMOUNT) ||
                            getMinimumAmountMessage(),
                    }}
                    defaultValue=""
                    render={({field}) => {
                        return (
                            <InputAndSelect
                                amountValue={field.value as number}
                                onAmountChange={(value) => {
                                    field.onChange(value);
                                    handleDataUpdate("amount", value);
                                }}
                                width="100%"
                                placeholder="Amount"
                                options={walletToDebit}
                                selectValue={"GHS"}
                            />
                        );
                    }}
                />
                {<Error>{
                    isBelowMinimumAmount
                        ? getMinimumAmountMessage()
                        : errors?.amount?.message
                }</Error>}
            </div>
            <div>
                <Controller
                    name="paymentMethod"
                    control={control}
                    rules={{required: "Payment method is required"}}
                    render={({field}) => {
                        return (
                            <CustomSelect
                                {...field}
                                options={ghsPayoutMethods}
                                $width="100%"
                                placeholder="Select payment method"
                                lightBorder
                            />
                        );
                    }}
                />
                {<Error>{errors?.paymentMethod?.message}</Error>}
            </div>


            <section>
                <RateBox>
          <span className="fee-box">
            +
            <span>
              {formatCurrencyAmount("GHS", userData?.fees ?? 0, 2) ?? 0}
            </span>
          </span>
                    <span>Processing fees</span>
                </RateBox>
            </section>

            {userData.amount && (
                <section>
                    <RateBox>
            <span className="fee-box">
              <span>
                {formatCurrencyAmount("GHS", userData.totalAmount, 2) ?? 0}
              </span>
            </span>
                        <span>Total amount</span>
                    </RateBox>
                </section>
            )}

            {hasBalanceError && (
                <BalanceExceptionWrapper>
                    <NotifyIcon/> <span>Exceeds your wallet balance of {formatCurrencyAmount("GHS", walletBalance, 2) ?? 0}.</span>
                    <Link to="/wallets/ghs"> Top-up funds</Link>
                </BalanceExceptionWrapper>
            )}

            <Button
                height="48px"
                label="Continue"
                disabled={hasBalanceError || !canContinueWithAmount || loading || !isValid}
                onClick={handleSubmit(handleNextStep)}
            />

            <Loader isLoading={loading}/>
        </ContentWrapper>
    );
};
export default Amount;
